import {
    RefObject, useCallback, useEffect, useRef,
} from 'react';

type Listener = (isIntersecting: boolean) => void;

export type Observe = (element: Element, onChange: Listener) => () => void;

const useSharedObserver = (rootRef: RefObject<Element>, rootMargin: string): Observe => {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const listenersRef = useRef(new Map<Element, Listener>());

    useEffect(() => () => {
        observerRef.current?.disconnect();
        observerRef.current = null;
    }, []);

    return useCallback<Observe>((element, onChange) => {
        if (!observerRef.current) {
            observerRef.current = new IntersectionObserver((entries) => {
                entries.forEach((entry) => listenersRef.current.get(entry.target)?.(entry.isIntersecting));
            }, { root: rootRef.current, rootMargin });
        }
        const observer = observerRef.current;
        listenersRef.current.set(element, onChange);
        observer.observe(element);
        return () => {
            listenersRef.current.delete(element);
            observer.unobserve(element);
        };
    }, [rootRef, rootMargin]);
};

export default useSharedObserver;
